/**
 * Pure helpers over the org chart: the tree, a person's managers, search, and
 * the "to be confirmed" test. No I/O; every component reads through these.
 */
import { OrgChart, OrgPosition, OrgRoute, Tier } from '../../services/orgChartApi';

export const TIER_ORDER: Tier[] = ['ELT', 'SLT', 'MANAGER', 'STAFF', 'VACANT'];

/** Short text shown on every card next to the colour: the colour is never the only cue. */
export const TIER_SHORT: Record<Tier, string> = {
  ELT: 'ELT',
  SLT: 'SLT',
  MANAGER: 'Director / Manager',
  STAFF: 'Staff',
  VACANT: 'Vacant',
};

export function childrenOf(chart: OrgChart): Map<string | null, OrgPosition[]> {
  const out = new Map<string | null, OrgPosition[]>();
  for (const p of chart.positions) {
    const list = out.get(p.reportsTo) || [];
    list.push(p);
    out.set(p.reportsTo, list);
  }
  return out;
}

export function byId(chart: OrgChart): Map<string, OrgPosition> {
  return new Map(chart.positions.map((p) => [p.id, p]));
}

/** The managers above a position, nearest first. Stops on a cycle rather than looping. */
export function managersOf(chart: OrgChart, id: string): OrgPosition[] {
  const index = byId(chart);
  const out: OrgPosition[] = [];
  const seen = new Set<string>([id]);
  let cur = index.get(id);
  while (cur && cur.reportsTo && !seen.has(cur.reportsTo)) {
    seen.add(cur.reportsTo);
    const up = index.get(cur.reportsTo);
    if (!up) break;
    out.push(up);
    cur = up;
  }
  return out;
}

/** What a person is called on screen. A vacancy says so in words. */
export function displayName(p: OrgPosition): string {
  return p.vacant || !p.name ? 'Vacant position' : p.name;
}

/** Inferred or flagged for confirmation anywhere on the position. */
export function toBeConfirmed(p: OrgPosition): boolean {
  return p.confirm.length > 0 || p.workspaces.some((w) => w.inferred);
}

export function routeHasInferred(r: OrgRoute): boolean {
  return [...r.primary, ...r.backup].some((c) => c.inferred);
}

const norm = (s: string | null | undefined) => (s || '').toLowerCase();

/** Does a free-text query match this position (name, titles, email, areas, workspaces)? */
export function matchesPosition(p: OrgPosition, query: string): boolean {
  const q = norm(query).trim();
  if (!q) return true;
  const hay = [p.name, p.chartTitle, p.rosterTitle, p.email, ...p.areas, ...p.workspaces.map((w) => `${w.workspace} ${w.as}`)]
    .map(norm)
    .join(' | ');
  return q.split(/\s+/).every((word) => hay.includes(word));
}

export function matchesRoute(r: OrgRoute, query: string, chart: OrgChart): boolean {
  const q = norm(query).trim();
  if (!q) return true;
  const index = byId(chart);
  const names = [...r.primary, ...r.backup].map((c) => index.get(c.position)?.name || '');
  const hay = [r.topic, ...(r.examples || []), ...names].map(norm).join(' | ');
  return q.split(/\s+/).every((word) => hay.includes(word));
}

/**
 * The positions to show for a query: every match plus its managers, so a match
 * is always seen in place. `null` means no filter.
 */
export function visibleFor(chart: OrgChart, query: string): Set<string> | null {
  if (!query.trim()) return null;
  const keep = new Set<string>();
  for (const p of chart.positions) {
    if (!matchesPosition(p, query)) continue;
    keep.add(p.id);
    for (const m of managersOf(chart, p.id)) keep.add(m.id);
  }
  return keep;
}
